import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, Phone, MapPin, ShieldCheck, CheckCircle2, Users, Sparkles,
  Share2, Heart, Quote, ChevronDown, Star, Clock, Trees, ShoppingBag, HeartPulse, Train,
  Bed, Bath, Home as HomeIcon, Car, Layers, Calendar,
  ArrowDownToLine, DoorOpen, ShowerHead, Lightbulb, Volume2, Lock, Accessibility,
} from "lucide-react";
import { PROPERTY_BY_SLUG, statusBadge } from "../data/properties";
import PropertyGallery from "../components/PropertyGallery";
import NotFoundPage from "./NotFoundPage";
import { useSEO } from "../lib/seo";
import { trackPhoneClick } from "../lib/tracking";
import useParallax from "../lib/useParallax";
import SaveHomeButton from "../components/SaveHomeButton";

const LIFESTYLE_ICONS = {
  "Cafés & dining": ShoppingBag,
  "Shopping": ShoppingBag,
  "Green space": Trees,
  "Parks & open space": Trees,
  "Transport": Train,
  "Connection": Train,
  "Healthcare": HeartPulse,
};

const QF_ICONS = {
  Bed,
  Bath,
  Layers,
  Users,
  Clock,
  Calendar,
  Car,
  HomeIcon,
};

// Match accessibility feature TITLES (case-insensitive substring) to icons
// for visual identification on the property accessibility grid.
const ACCESSIBILITY_ICON_MAP = [
  { match: /step-?free|level entry|threshold/i, Icon: ArrowDownToLine },
  { match: /doorway|door width|wide internal/i, Icon: DoorOpen },
  { match: /shower|walk-?in/i, Icon: ShowerHead },
  { match: /vanity|bathroom|amenities|bathing/i, Icon: Bath },
  { match: /lighting|led/i, Icon: Lightbulb },
  { match: /acoustic|sound|quiet|low-?stim|sensory/i, Icon: Volume2 },
  { match: /privacy|secure|private/i, Icon: Lock },
  { match: /single-?level|outdoor|lawn|yard|garden/i, Icon: Trees },
];

function pickAccessibilityIcon(title) {
  for (const m of ACCESSIBILITY_ICON_MAP) {
    if (m.match.test(title)) return m.Icon;
  }
  return Accessibility;
}

function QuickFactsBar({ p }) {
  if (!p.quickFacts?.length) return null;
  const isAvailable = p.status === "available";
  return (
    <section
      className="border-b border-[color:var(--alpha-line)] bg-white"
      data-testid={`property-${p.slug}-quickfacts`}
    >
      <div className="alpha-container py-4 md:py-5">
        <div className="flex items-center gap-3 overflow-x-auto md:flex-wrap md:overflow-visible no-scrollbar">
          {/* Status pill — pulses when available */}
          <span
            className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-bold uppercase tracking-[0.16em] ${
              isAvailable
                ? "bg-[color:var(--alpha-green-50)] text-[color:var(--alpha-green-700)] border border-[color:var(--alpha-green-200)]"
                : p.status === "upcoming"
                ? "bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] border border-[color:var(--alpha-purple-200)]"
                : "bg-[color:var(--alpha-bg-soft)] text-[color:var(--alpha-mute)] border border-[color:var(--alpha-line)]"
            }`}
          >
            <span className="relative flex h-2 w-2">
              {isAvailable && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--alpha-green-500)] opacity-75" />
              )}
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  isAvailable
                    ? "bg-[color:var(--alpha-green-600)]"
                    : p.status === "upcoming"
                    ? "bg-[color:var(--alpha-purple-600)]"
                    : "bg-[color:var(--alpha-mute)]"
                }`}
              />
            </span>
            {isAvailable ? "Available now" : p.status === "upcoming" ? "Coming soon" : "Filled"}
          </span>

          {/* Suburb anchor */}
          <span className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[color:var(--alpha-ink)]">
            <MapPin className="w-3.5 h-3.5 text-[color:var(--alpha-purple-700)]" />
            {p.suburb}, {p.state}
          </span>

          {/* Subtle divider */}
          <span className="hidden md:inline-block h-5 w-px bg-[color:var(--alpha-line)]" aria-hidden />

          {/* Quick facts */}
          {p.quickFacts.map((qf, i) => {
            const Icon = QF_ICONS[qf.Icon] || HomeIcon;
            return (
              <span
                key={`${qf.k}-${i}`}
                className="shrink-0 inline-flex items-center gap-2 text-[12.5px] text-[color:var(--alpha-ink)]"
                data-testid={`property-${p.slug}-qf-${qf.k.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="w-7 h-7 rounded-lg bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="leading-tight">
                  <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-[color:var(--alpha-mute)]">{qf.k}</span>
                  <span className="block font-semibold">{qf.v}</span>
                </span>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CinematicHero({ p, sb }) {
  const [imgRef, imgY] = useParallax({ speed: 0.12, max: 60 });
  return (
    <section className="relative h-[78vh] min-h-[560px] md:h-[88vh] md:min-h-[640px] overflow-hidden text-white">
      {/* Cinematic background image */}
      <div ref={imgRef} className="absolute inset-0 will-change-transform" style={{ transform: `translate3d(0, ${imgY}px, 0)` }}>
        <img
          src={p.images[0].src}
          alt={p.images[0].alt}
          className="absolute inset-0 w-full h-full object-cover alpha-ken-burns"
          loading="eager"
        />
      </div>
      {/* Stacked overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0625]/40 via-[#0F0625]/55 to-[#0F0625]/95" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#1B0B3D]/55 via-transparent to-[#4FA56E]/10 mix-blend-multiply" />
      <div className="alpha-flow-lines" aria-hidden />
      <div className="alpha-particles" aria-hidden />
      <div className="alpha-grain-dark" aria-hidden />

      {/* Floating brand glow */}
      <div className="absolute -left-20 top-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-a"
        style={{ background: "radial-gradient(closest-side, rgba(134,89,199,0.55), transparent 70%)", filter: "blur(50px)" }} aria-hidden />
      <div className="absolute -right-20 bottom-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-b"
        style={{ background: "radial-gradient(closest-side, rgba(79,165,110,0.45), transparent 70%)", filter: "blur(50px)" }} aria-hidden />

      <div className="relative h-full alpha-container flex flex-col justify-end pb-12 md:pb-20">
        {/* Save button — top right of hero */}
        <div className="absolute top-6 right-4 md:right-8 z-10">
          <SaveHomeButton slug={p.slug} homeName={p.name} variant="overlay" size="lg" testid={`property-${p.slug}-hero-save`} />
        </div>

        <div className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-2 mb-5 alpha-fade-up">
            <span className={`alpha-glass-dark text-white !py-1.5 !px-3 !text-[11px] uppercase tracking-[0.22em] rounded-full inline-flex items-center gap-1.5 ${
              sb.tone === "green" ? "!bg-[color:var(--alpha-green-600)]/85" : sb.tone === "purple" ? "!bg-[color:var(--alpha-purple-600)]/85" : ""
            }`}>
              <Sparkles className="w-3 h-3" /> {sb.label}
            </span>
            {p.badges.slice(0, 2).map((b) => (
              <span key={b.label} className="alpha-glass-dark text-white !py-1.5 !px-3 !text-[11px] uppercase tracking-[0.18em] rounded-full">
                {b.label}
              </span>
            ))}
          </div>

          <div className="text-[11px] tracking-[0.32em] uppercase text-white/70 font-semibold mb-3 alpha-fade-up alpha-delay-1 alpha-press">
            <MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />{p.suburb}, {p.state} · Supported Independent Living
          </div>
          <h1 className="font-display font-medium tracking-tight leading-[0.98] alpha-press alpha-fade-up alpha-delay-2"
              style={{ fontSize: "clamp(38px, 6.5vw, 84px)" }}>
            <span className="!text-white">{p.name.replace(/^The\s/, "")}</span>
            <span className="block mt-2 text-white/70 font-display !text-white/70" style={{ fontSize: "clamp(18px, 1.6vw, 22px)" }}>
              <span className="alpha-serif-italic alpha-shimmer-text">{p.headline}</span>
            </span>
          </h1>

          <p className="mt-5 text-[15.5px] md:text-lg text-white/85 max-w-2xl alpha-fade-up alpha-delay-3 alpha-press">
            {p.oneLiner}
          </p>

          <div className="mt-7 flex flex-wrap gap-2.5 alpha-fade-up alpha-delay-4">
            <Link to={`/homes/${p.slug}/enquire`} className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
              {p.status === "upcoming" ? "Register interest" : "Enquire now"} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `property-${p.slug}-hero`)} className="alpha-glass-dark border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/15 transition-colors">
              <Phone className="w-4 h-4" /> 1300 380 692
            </a>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-5 hidden md:flex flex-col items-center text-white/60 text-[10px] tracking-[0.24em] uppercase font-semibold">
          <span>Discover</span>
          <ChevronDown className="w-4 h-4 mt-1 animate-bounce" />
        </div>
      </div>
    </section>
  );
}

function StorySection({ side = "right", eyebrow, title, body, image, imageAlt = "" }) {
  const [parRef, parY] = useParallax({ speed: 0.08, max: 36 });
  return (
    <section className="alpha-section-tight relative">
      <div className="alpha-container grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className={`lg:col-span-6 alpha-curtain ${side === "right" ? "" : "lg:order-2"}`}>
          <div className="alpha-overline">{eyebrow}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[42px] font-medium tracking-tight leading-[1.05]">
            {title}
          </h2>
          <p className="mt-4 text-[color:var(--alpha-mute)] text-[15.5px] leading-relaxed max-w-xl">
            {body}
          </p>
        </div>
        <div className={`lg:col-span-6 alpha-curtain ${side === "right" ? "" : "lg:order-1"}`}>
          <div ref={parRef} className="relative rounded-[22px] md:rounded-[28px] overflow-hidden alpha-ring-gradient shadow-2xl aspect-[5/4]">
            <img
              src={image}
              alt={imageAlt}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover will-change-transform"
              style={{ transform: `translate3d(0, ${parY}px, 0) scale(1.06)` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B0B3D]/30 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PropertyPage({ slug: slugProp } = {}) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const p = PROPERTY_BY_SLUG[slug];

  useSEO({
    title: p ? `${p.name} · SIL ${p.suburb}, ${p.state} | NDIS Vacancy · Alpha Community Care` : "Home not found | Alpha Community Care",
    description: p ? `${p.name} — accessible SIL ${p.suburb}, ${p.state}. ${p.oneLiner} Registered NDIS provider · 1:1 and 1:2 support only · Melbourne hub.` : undefined,
    keywords: p ? [
      `SIL ${p.suburb}`, `SIL ${p.state}`, "SIL Melbourne", "SIL Victoria",
      "SDA Melbourne", "NDIS housing Melbourne", "NDIS provider Melbourne",
      "Supported Independent Living Melbourne", "NDIS vacancy", `${p.name}`,
    ] : undefined,
    canonical: p ? `https://alphacommunitycare.com.au${p.href}` : undefined,
    jsonLd: p ? [
      {
        "@context": "https://schema.org",
        "@type": "Accommodation",
        "@id": `https://alphacommunitycare.com.au${p.href}`,
        "name": p.name,
        "description": p.oneLiner,
        "additionalType": "https://schema.org/House",
        "numberOfBedrooms": p.specs?.find?.((s) => /bed|layout/i.test(s.k))?.v || undefined,
        "numberOfBathroomsTotal": undefined,
        "image": p.images.map((i) => `https://alphacommunitycare.com.au${i.src}`),
        "amenityFeature": (p.features || []).map((f) => ({ "@type": "LocationFeatureSpecification", "name": f })),
        "address": {
          "@type": "PostalAddress",
          "addressLocality": p.suburb,
          "addressRegion": p.state,
          "addressCountry": "AU",
        },
        "provider": {
          "@type": "Organization",
          "@id": "https://alphacommunitycare.com.au/#org",
          "name": "Alpha Community Care",
          "url": "https://alphacommunitycare.com.au/",
          "telephone": "+61-1300-380-692",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://alphacommunitycare.com.au/" },
          { "@type": "ListItem", "position": 2, "name": "Our Homes", "item": "https://alphacommunitycare.com.au/vacancies" },
          { "@type": "ListItem", "position": 3, "name": p.name, "item": `https://alphacommunitycare.com.au${p.href}` },
        ],
      },
    ] : undefined,
  });

  if (!p) return <NotFoundPage />;

  const sb = statusBadge(p);
  const isUpcoming = p.status === "upcoming";

  const onShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const payload = { title: p.name, text: p.oneLiner, url };
    // navigator.share + navigator.clipboard both throw on user-cancel —
    // that's expected, not an error worth surfacing to the participant.
    if (navigator.share) {
      try { await navigator.share(payload); } catch (e) { /* user cancelled — intentional no-op */ }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        alert("Link copied");
      } catch (e) {
        // Clipboard blocked (insecure context / permission denied) — show the URL so the user can copy manually.
        alert(`Copy this link:\n${url}`);
      }
    }
  };

  return (
    <div data-testid={`page-property-${p.slug}`} className="bg-white">
      {/* Cinematic hero */}
      <CinematicHero p={p} sb={sb} />

      {/* Sticky breadcrumb / share — appears under hero */}
      <div className="border-b border-[color:var(--alpha-line)] bg-white sticky top-0 z-30 backdrop-blur-md/95">
        <div className="alpha-container py-3 flex items-center justify-between gap-3 text-xs">
          <nav className="flex items-center gap-1.5 text-[color:var(--alpha-mute)] truncate">
            <Link to="/" className="hover:text-[color:var(--alpha-purple-700)]">Home</Link>
            <span>/</span>
            <Link to="/vacancies" className="hover:text-[color:var(--alpha-purple-700)]">Our Homes</Link>
            <span>/</span>
            <span className="text-[color:var(--alpha-ink)] truncate font-semibold">{p.name}</span>
          </nav>
          <div className="flex items-center gap-2">
            <Link to={`/homes/${p.slug}/enquire`} className="alpha-btn-primary !py-2 !px-4 text-xs hidden sm:inline-flex" data-testid={`property-${p.slug}-stickycta`}>
              {isUpcoming ? "Register interest" : "Enquire"}
            </Link>
            <SaveHomeButton
              slug={p.slug}
              homeName={p.name}
              variant="solid"
              testid={`property-${p.slug}-save`}
              className="!py-2 !px-3 !text-xs hidden sm:inline-flex"
            />
            <button onClick={onShare} className="alpha-btn-ghost !py-2 !px-3 text-xs" data-testid={`property-${p.slug}-share`}>
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </div>

      {/* Real-estate-style quick facts ribbon */}
      <QuickFactsBar p={p} />

      {/* Gallery */}
      <section className="alpha-section-tight relative">
        <div className="alpha-mesh -z-0 hidden md:block" aria-hidden style={{ inset: "-20%", opacity: 0.4 }} />
        <div className="alpha-container relative">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-5">
            <div>
              <div className="alpha-overline">The home in pictures</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium">{p.images.length} cinematic frames</h2>
            </div>
            <div className="text-xs text-[color:var(--alpha-mute)]">Tap to expand · swipe on mobile</div>
          </div>
          <PropertyGallery images={p.images} testid={`property-${p.slug}-gallery`} />
        </div>
      </section>

      {/* Storytelling sections */}
      {p.story?.designedFor && (
        <StorySection
          side="right"
          eyebrow={p.story.designedFor.eyebrow}
          title={p.story.designedFor.title}
          body={p.story.designedFor.body}
          image={p.story.designedFor.image}
          imageAlt={p.images.find((i) => i.src === p.story.designedFor.image)?.alt || ""}
        />
      )}
      {p.story?.indoorOutdoor && (
        <section className="relative bg-[color:var(--alpha-bg-soft)]">
          <StorySection
            side="left"
            eyebrow={p.story.indoorOutdoor.eyebrow}
            title={p.story.indoorOutdoor.title}
            body={p.story.indoorOutdoor.body}
            image={p.story.indoorOutdoor.image}
            imageAlt={p.images.find((i) => i.src === p.story.indoorOutdoor.image)?.alt || ""}
          />
        </section>
      )}
      {p.story?.selfCare && (
        <StorySection
          side="right"
          eyebrow={p.story.selfCare.eyebrow}
          title={p.story.selfCare.title}
          body={p.story.selfCare.body}
          image={p.story.selfCare.image}
          imageAlt={p.images.find((i) => i.src === p.story.selfCare.image)?.alt || ""}
        />
      )}

      {/* Premium accessibility features */}
      {p.accessibility && (
        <section className="relative overflow-hidden alpha-section-tight bg-[#0F0625] text-white">
          <div className="alpha-mesh alpha-mesh-dark" aria-hidden />
          <div className="alpha-particles" aria-hidden />
          <div className="alpha-flow-lines" aria-hidden />
          <div className="alpha-container relative">
            <div className="max-w-2xl alpha-curtain">
              <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-white/70">
                Built-in accessibility
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-[44px] font-medium leading-[1.05] !text-white">
                Calm, dignified, and built around <span className="alpha-shimmer-text">you</span>.
              </h2>
              <p className="mt-3 text-white/75 text-[15px] max-w-xl">
                Every detail chosen so daily living feels gentle — not clinical.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {p.accessibility.map((a, i) => {
                const FeatureIcon = pickAccessibilityIcon(a.title);
                return (
                  <div
                    key={a.title}
                    className="alpha-glass-dark rounded-2xl p-5 hover:bg-white/[0.10] transition-colors alpha-curtain"
                    style={{ transitionDelay: `${i * 60}ms` }}
                    data-testid={`property-${p.slug}-a11y-${i}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white shrink-0">
                        <FeatureIcon className="w-5 h-5 text-[color:var(--alpha-green-500)]" aria-hidden />
                      </span>
                      <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-white/55">
                        Built-in
                      </span>
                    </div>
                    <h3 className="mt-3.5 font-display text-lg font-semibold !text-white leading-tight">{a.title}</h3>
                    <p className="mt-1 text-sm text-white/70 leading-relaxed">{a.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why participants love this home — pull quote */}
      {p.whyLove && (
        <section className="alpha-section-tight relative">
          <div className="alpha-container">
            <div className="alpha-overline alpha-curtain">Why participants love this home</div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6 mt-6">
              {p.whyLove.map((line, i) => (
                <figure key={line} className="alpha-card p-7 md:p-8 relative overflow-hidden alpha-curtain hover:-translate-y-1"
                  style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full alpha-gradient opacity-10 blur-2xl" aria-hidden />
                  <Quote className="w-6 h-6 text-[color:var(--alpha-purple-600)] mb-3" />
                  <blockquote className="font-display text-[20px] md:text-[22px] leading-snug">{line}</blockquote>
                  <div className="mt-4 flex gap-0.5 text-[color:var(--alpha-green-600)]">
                    {Array.from({ length: 5 }, (_, k) => (
                      <Star key={`star-${k}`} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ideal for */}
      {p.idealFor && (
        <section className="alpha-section-tight relative bg-[color:var(--alpha-bg-soft)]">
          <div className="alpha-container">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 alpha-curtain">
                <div className="alpha-overline">Ideal for</div>
                <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium leading-[1.05]">
                  Who this home <span className="alpha-serif-italic alpha-gradient-text">truly suits</span>.
                </h2>
                <p className="mt-3 text-[color:var(--alpha-mute)] max-w-md text-[15px]">
                  Talk to our intake team — we'll match the home to the right participant, never the other way around.
                </p>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  <Link to={`/homes/${p.slug}/enquire`} className="alpha-btn-primary text-sm !py-2.5 !px-5">
                    {isUpcoming ? "Register interest" : "Enquire about this home"} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `property-${p.slug}-idealfor`)} className="alpha-btn-green text-sm !py-2.5 !px-5">
                    <Phone className="w-4 h-4" /> 1300 380 692
                  </a>
                </div>
              </div>
              <div className="lg:col-span-7">
                <ul className="grid sm:grid-cols-2 gap-2.5">
                  {p.idealFor.map((line, i) => (
                    <li key={line} className="alpha-card !rounded-xl p-4 flex items-start gap-3 alpha-curtain hover:-translate-y-0.5"
                      style={{ transitionDelay: `${i * 60}ms` }}>
                      <span className="w-9 h-9 rounded-lg bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-medium text-[color:var(--alpha-ink)] mt-1.5">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Lifestyle & nearby */}
      {p.lifestyle && (
        <section className="alpha-section-tight relative">
          <div className="alpha-container">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7 alpha-curtain">
                <div className="alpha-overline">Lifestyle & nearby amenities</div>
                <h2 className="mt-3 font-display text-3xl sm:text-[42px] font-medium leading-[1.05]">
                  {p.lifestyle.headline}
                </h2>
                <p className="mt-3 text-[color:var(--alpha-mute)] max-w-xl text-[15px]">{p.lifestyle.blurb}</p>
              </div>
              <div className="lg:col-span-5 text-sm text-[color:var(--alpha-mute)]">
                <div className="flex items-center gap-2 text-[color:var(--alpha-ink)] font-semibold">
                  <MapPin className="w-4 h-4 text-[color:var(--alpha-purple-700)]" />
                  <span>{p.suburb}, {p.state}</span>
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.22em] text-[color:var(--alpha-mute)]">
                  Curated for everyday living
                </div>
              </div>
            </div>
            <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {p.lifestyle.points.map((pt, i) => {
                const Icon = LIFESTYLE_ICONS[pt.k] || Clock;
                return (
                  <div key={pt.k} className="alpha-card p-5 relative overflow-hidden alpha-curtain hover:-translate-y-1"
                    style={{ transitionDelay: `${i * 70}ms` }}>
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full alpha-gradient opacity-10 blur-xl" aria-hidden />
                    <span className="w-10 h-10 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)]">{pt.k}</div>
                    <div className="mt-1 font-display font-semibold text-[15px] leading-tight">{pt.v}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Final cinematic CTA */}
      <section className="relative overflow-hidden">
        <div className="alpha-section-tight relative">
          <div className="alpha-container">
            <div className="relative overflow-hidden rounded-[24px] md:rounded-[36px] alpha-gradient text-white p-8 md:p-14">
              <div className="alpha-mesh" style={{ opacity: 0.55 }} aria-hidden />
              <div className="alpha-particles" aria-hidden />
              <div className="alpha-grain-dark" aria-hidden />
              <div className="relative grid md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-7">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                    <Sparkles className="w-3.5 h-3.5" /> {sb.label} · {p.suburb}
                  </div>
                  <h2 className="mt-4 font-display text-3xl md:text-[48px] font-medium leading-[1.04] !text-white">
                    Think this could be <span className="alpha-serif-italic">the one</span>?
                  </h2>
                  <p className="mt-3 text-white/85 text-[15.5px] max-w-xl">
                    Tell us a little about the participant and we'll line up a time that works.
                  </p>
                </div>
                <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                  <Link to={`/homes/${p.slug}/enquire`} className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2">
                    {isUpcoming ? "Register interest" : "Enquire now"} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `property-${p.slug}-cta`)} className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4" /> 1300 380 692
                  </a>
                  <Link to="/vacancies" className="text-white/85 text-center text-sm hover:text-white py-2 inline-flex items-center justify-center gap-1.5">
                    Browse all homes <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 bg-gradient-to-t from-white via-white to-white/70 backdrop-blur-sm" data-testid={`property-${p.slug}-mobile-cta`}>
        <div className="flex gap-2 max-w-md mx-auto">
          <a
            href="tel:1300380692"
            onClick={() => trackPhoneClick("1300 380 692", `property-${p.slug}-mobile-bar`)}
            className="alpha-btn-green flex-1 justify-center !py-3 !px-4 text-sm shadow-lg"
            aria-label="Call 1300 380 692"
          >
            <Phone className="w-4 h-4" /> Call
          </a>
          <Link
            to={`/homes/${p.slug}/enquire`}
            className="alpha-btn-primary flex-[1.6] justify-center !py-3 !px-4 text-sm shadow-lg"
          >
            {isUpcoming ? "Register interest" : "Enquire now"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
